import { AuthService } from "@service/auth.service";
import { ActivityType } from "@models/activity-type";

const BASE_API_URL = process.env.REACT_APP_API_URL;

export type CognitiveDomainExport =
  | "memory"
  | "attention"
  | "processing_speed"
  | "language"
  | "cognitive_flexibility"
  | "spatial";

export interface ExportGamesParams {
  patientIds: string[];
  from: string;
  to: string;
  domain?: CognitiveDomainExport;
  gameId?: string;
}

export interface ExportConversationsParams {
  patientIds: string[];
  from: string;
  to: string;
  activityTypes?: ActivityType[];
  includeTranscript?: boolean;
}

async function postExportCsv(
  path: string,
  body: ExportGamesParams | ExportConversationsParams,
): Promise<{ blob: Blob; filename: string }> {
  const token = await AuthService.instance.getAccessToken();
  const res = await fetch(`${BASE_API_URL}/patients/exports/${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: token } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const err = await res.json();
      message = err.message ?? message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  const blob = await res.blob();
  const fromHeader = res.headers.get("Content-Disposition");
  const filename =
    (fromHeader && /filename="([^"]+)"/.exec(fromHeader)?.[1]) ||
    `${path}-${new Date().toISOString()}.csv`;
  return { blob, filename };
}

export async function exportPatientGames(
  params: ExportGamesParams,
): Promise<{ blob: Blob; filename: string }> {
  return postExportCsv("games", params);
}

export async function exportPatientConversations(
  params: ExportConversationsParams,
): Promise<{ blob: Blob; filename: string }> {
  return postExportCsv("conversations", params);
}

export const EXPORT_ACTIVE_GAME_IDS = [
  "sequence-recall",
  "n-back",
  "word-fluency-tap",
  "math-blitz",
  "rule-switch",
  "pattern-match",
  "reaction-tap",
  "target-focus",
  "path-memory",
  "category-tap",
] as const;

/** i18n keys under games.library.* */
export const EXPORT_GAME_I18N_KEYS: Record<string, string> = {
  "sequence-recall": "sequenceRecall",
  "n-back": "nBack",
  "word-fluency-tap": "wordFluencyTap",
  "math-blitz": "mathBlitz",
  "rule-switch": "ruleSwitch",
  "pattern-match": "patternMatch",
  "reaction-tap": "reactionTap",
  "target-focus": "targetFocus",
  "path-memory": "pathMemory",
  "category-tap": "categoryTap",
};

export const EXPORT_DOMAIN_KEYS: CognitiveDomainExport[] = [
  "memory",
  "attention",
  "processing_speed",
  "language",
  "cognitive_flexibility",
  "spatial",
];
